'use client';

import React, { useMemo, useState } from 'react';
import Link from 'next/link';
import { Check, ChevronRight } from 'lucide-react';

import { platformModules, solutions } from '@/components/marketing/marketing-data';

const groups = [
  { title: 'Core HR', items: platformModules.slice(0, 4) },
  {
    title: 'Payroll & Compliance',
    items: [
      platformModules[4],
      { ...platformModules[8], title: 'TDS & Tax' },
      { ...platformModules[4], title: 'Salary Structures', slug: 'payroll' },
    ],
  },
  { title: 'Talent', items: [platformModules[5], platformModules[6]] },
  { title: 'Operations', items: solutions.slice(0, 4) },
  { title: 'Intelligence', items: [platformModules[8], platformModules[9]] },
];

const all = groups.flatMap((group) => group.items).map((item) => item.title);

export default function PricingPage() {
  const [selected, setSelected] = useState<string[]>([
    'Employee Management',
    'Attendance Management',
    'Payroll Management',
  ]);

  const toggle = (name: string) => {
    setSelected((current) =>
      current.includes(name) ? current.filter((value) => value !== name) : [...current, name]
    );
  };

  const selectAll = () => setSelected(all);
  const clearAll = () => setSelected([]);

  const progress = useMemo(
    () => Math.max(0, Math.min(100, Math.round((selected.length / all.length) * 100))),
    [selected.length]
  );

  return (
    <>
      <section id="module-selection" className="scroll-mt-24 bg-[#F7F8FA] py-16 sm:py-20">
        <div className="mx-auto grid max-w-[1320px] gap-8 px-5 lg:grid-cols-[1.1fr_.9fr] lg:px-8">
          <div className="space-y-7">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-orange-500">
                  Build Your OneHR
                </div>
                <h2 className="mt-2 text-2xl font-bold">Choose your capabilities</h2>
                <p className="mt-1 text-sm text-slate-500">
                  {selected.length} capabilities selected
                </p>
              </div>

              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={selectAll}
                  className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-bold"
                >
                  Select All
                </button>
                <button
                  type="button"
                  onClick={clearAll}
                  className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-500"
                >
                  Clear All
                </button>
              </div>
            </div>

            {groups.map((group) => (
              <div key={group.title} className="rounded-2xl border border-slate-200 bg-white p-4">
                <div className="mb-3 text-[10px] font-bold uppercase tracking-[0.18em] text-slate-600">
                  {group.title}
                </div>

                <div className="grid gap-2 sm:grid-cols-2">
                  {group.items.map((item) => {
                    const checked = selected.includes(item.title);
                    return (
                      <button
                        key={`${group.title}-${item.title}`}
                        type="button"
                        onClick={() => toggle(item.title)}
                        className={[
                          'flex items-center gap-3 rounded-xl border p-3 text-left transition-all',
                          checked
                            ? 'border-orange-300 bg-orange-50/70'
                            : 'border-slate-200 hover:bg-slate-50',
                        ].join(' ')}
                      >
                        <span
                          className={[
                            'flex h-5 w-5 shrink-0 items-center justify-center rounded-full border',
                            checked
                              ? 'border-orange-500 bg-orange-500 text-white'
                              : 'border-slate-300',
                          ].join(' ')}
                        >
                          {checked ? <Check size={12} /> : null}
                        </span>
                        <span className="text-xs font-semibold text-[#0A1C33]">{item.title}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          <div className="lg:sticky lg:top-24 lg:self-start">
            <div className="rounded-[28px] bg-[#06162C] p-6 text-white shadow-[0_30px_80px_rgba(9,30,66,.18)] sm:p-7">
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-orange-300">
                Your Solution
              </div>
              <h3 className="mt-3 text-2xl font-bold">Get a custom quote</h3>
              <p className="mt-2 text-sm leading-6 text-white/65">
                No public pricing. We&apos;ll recommend the right combination of modules for your
                workforce.
              </p>

              <div className="mt-6 rounded-2xl bg-white/[0.05] p-4">
                <div className="flex justify-between text-xs">
                  <span className="text-white/65">Capabilities selected</span>
                  <span className="font-bold">{selected.length}</span>
                </div>

                <div className="mt-4 h-2 overflow-hidden rounded-full bg-white/10">
                  <div
                    className="h-full rounded-full bg-orange-500 transition-all"
                    style={{ width: `${progress}%` }}
                  />
                </div>

                <div className="mt-4 flex flex-wrap gap-2">
                  {selected.length === 0 ? (
                    <span className="text-xs text-white/30">Choose the capabilities you need.</span>
                  ) : (
                    selected.map((item) => (
                      <span
                        key={item}
                        className="rounded-full border border-white/10 bg-white/5 px-2.5 py-1.5 text-[10px] text-white/65"
                      >
                        {item}
                      </span>
                    ))
                  )}
                </div>
              </div>

              <div className="mt-6 space-y-3">
                {[
                  'Flexible module combinations',
                  'Custom implementation',
                  'Enterprise support',
                  'No public pricing',
                ].map((item) => (
                  <div key={item} className="flex items-center gap-2 text-xs text-white/55">
                    <Check size={14} className="text-orange-300" />
                    {item}
                  </div>
                ))}
              </div>

              <Link
                href={`/contact?type=custom-quote&modules=${encodeURIComponent(selected.join(','))}`}
                className="mt-7 flex h-12 items-center justify-center rounded-full bg-[#c94e00] text-sm font-bold text-white"
              >
                Get My Custom Quote →
              </Link>

              <Link
                href="/request-demo"
                className="mt-3 flex h-12 items-center justify-center rounded-full border border-white/10 text-sm font-semibold text-white/75"
              >
                Talk to an Expert
              </Link>
            </div>

            <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-5">
              <div className="text-sm font-bold">Need help choosing?</div>
              <p className="mt-2 text-xs leading-6 text-slate-500">
                Tell us about your workforce, workflows and industry. We&apos;ll guide you to the
                right Mentrox setup.
              </p>
              <Link
                href="/contact"
                className="mt-4 inline-flex items-center gap-2 text-xs font-bold text-orange-600"
              >
                Contact Sales <ChevronRight size={13} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
